import { useSaveData } from '@entities/common/savedData/index.ts';
import { useCallback, useEffect, useLayoutEffect, } from 'react';
import { withTimeout } from '@shared/helpers/index.ts';

export const useCurrentPos = (nameInStorage: string, action: any) => {
  const handleClick = (...args: unknown[]) => {
    const currentScroll = window.scrollY;
    localStorage.setItem(nameInStorage, String(currentScroll));
    action(...args);
  };

  useEffect(() => {
    const scroll = localStorage.getItem(nameInStorage);
    scroll && window.scrollTo(0, Number(scroll));
    localStorage.removeItem(nameInStorage);
  }, []);

  return {
    handleClick,
  };
};
/**
 * useCurrentPosRtk - хук, который хранит в RTK состояние window.scrollY на момент вызова функции
 * saveCurrentPos.
 * @param {string} nameInStorage  - имя, по которому будут хранится данные (должно быть уникальным + неизменным для компонента).
 * @param {boolean} withScrollToSavedPos  - флаг, который отвечает за автоматический скролл на позицию, когда вызывался saveCurrentPos (default - false).
 *
 * @returns {function} saveCurrentPos - функция, которая сохраняет текущую позицию.
 * @returns {function} scrollToSavedPos - функция, которая скролит на позицию, сохранённую saveCurrentPos.
 */
export const useCurrentPosRtk = ({
  nameInStorage,
  withScrollToSavedPos = false,
  behavior = 'instant',
}: {
  nameInStorage: string;
  withScrollToSavedPos?: boolean;
  behavior?: ScrollBehavior;
}) => {
  const {
    data: scrollYPos,
    doRemoveStorageData,
    doSetStorageData,
  } = useSaveData<number>(nameInStorage);

  const saveCurrentPos = useCallback(() => {
    const scrollYPos = window.scrollY;
    scrollYPos && doSetStorageData(scrollYPos);
  }, [doSetStorageData]);

  const scrollToSavedPos = useCallback(() => {
    withTimeout(() => window.scrollTo({ top: scrollYPos || 0, behavior }));
    scrollYPos && doRemoveStorageData();
  }, [scrollYPos, doRemoveStorageData, behavior]);

  useLayoutEffect(() => {
    if (withScrollToSavedPos) {
      scrollToSavedPos();
    }
  }, [withScrollToSavedPos]);

  return {
    saveCurrentPos,
    scrollToSavedPos,
  };
};
